import { useCallback, useMemo } from "react"
import { useMultiPieGraphOption } from "./options"
import { useMultiPieGraphModel } from "./model"
import { numericValueFormat, percentFormat } from "../../common"
import { useStackLegend } from "../../../components/stackLegend"

type useMultiPieGraphRenderOptionProps<T> = {
	graphOption: ReturnType<typeof useMultiPieGraphOption<T>>
	graphData: ReturnType<typeof useMultiPieGraphModel<T>>
}
export const useMultiPieGraphRenderOption = <T,>({
	graphOption: {
		axis2Def, valueDef, row
	},
	graphData,
}: useMultiPieGraphRenderOptionProps<T>) => {
	const percentFormatter = useCallback(
		(params: { name: string, percent: number }) => percentFormat(params.name, params.percent, 1),
		[]
	)
	const valueFormatter = useCallback(
		(value: number | string) => numericValueFormat(value, 1, valueDef?.unit),
		[valueDef]
	)

  const legend = useStackLegend({ show: axis2Def ? true : false })

	const title = useMemo(
		() => {
			if (!graphData || graphData.series.length === 0) return undefined

			return graphData.series.map(
				(series) => ({
					text: (series.name === '' ? '(空)' : series.name) + '　',
					textAlign: 'center',
					textVerticalAlign: 'middle',
					textStyle: {
						fontSize: 12,
						fontWeight: 'normal',
						baseline: 'bottom',
					},
				})
			)
		},
		[graphData]
	)

	const series = useMemo(
		() => {
			if (!graphData) return []
			if (graphData.series.length === 0) {
				return [{
					type: 'pie',
					radius: '85%',
					center: ['40%', '50%'],
					label: {
						show: true,
						position: 'inside',
						formatter: percentFormatter
					},
					data: graphData.data.map((r) => ({
						code: r.code,
						name: r.name,
						value: r.value,
						itemStyle: r.itemStyle,
					}))
				}]
			}
			return graphData.series.map((series) => ({
				type: 'pie',
				radius: [`${graphData.radius * 0.5}%`, `${graphData.radius}%`],
				id: series.code,
				name: series.name,
				label: {
					show: true,
					position: 'inside',
					formatter: percentFormatter
				},
				data: graphData.data.map((r) => ({
					code: r.code,
					name: r.name,
					value: r.series[series.code],
					itemStyle: r.itemStyle,
				})),
			}))
		},
		[graphData, percentFormatter]
	)

	const media = useMemo(
		() => {
			if (!graphData || graphData.series.length === 0) return undefined

			const media = []
			for (let column = 1; column <= 20; column++) {
				media.push({
					query: {
						minAspectRatio: column / row
					},
					option: {
						title: graphData.series.map((_, i) => ({
							left: `${(((i % column) + 0.5) * 90) / column}%`,
							top: `${((Math.floor(i / column) + 0.5) * 100) / row}%`
						})),
						series: graphData.series.map((_, i) => ({
							center: [
								`${(((i % column) + 0.5) * 90) / column}%`,
								`${((Math.floor(i / column) + 0.5) * 100) / row}%`
							],
						}))
					},
				})
			}
			return media.length > 0 ? media : undefined
		},
		[graphData, row]
	)
	return {
		//animation: false,
		tooltip: {
			trigger: 'item',
			valueFormatter
		},
		title,
		grid: {
			left: 10,
			right: 10,
			top: 10,
			bottom: 10,
			containLabel: true,
		},
		legend,
		series, media,
		textStyle: {
			fontFamily: 'Noto Sans JP,sans-serif',
		}
	}
}